import React, { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { toAbsoluteUrl } from '../helpers/AssetHelpers'
import Turnstile, { isTurnstileConfigured } from '../common/Turnstile'
import contactService from '../../services/contact.service'
import './contact.css'

const SUPPORT_EMAIL = 'support@eyeconx.net'
const SUPPORT_PHONE = '1-866-533-2669'
const SUBJECT_OPTIONS = [
    'General inquiry',
    'Technical support',
    'Billing / invoices',
    'Account access',
    'Feature request',
    'Other',
]

function formatPhoneNumber(value) {
    const digits = String(value || '').replace(/\D/g, '').slice(0, 10)
    if (digits.length <= 3) return digits
    if (digits.length <= 6) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`
}

function ContactUs() {
    const {
        register,
        handleSubmit,
        reset,
        setValue,
        formState: { errors, isSubmitting },
    } = useForm({
        defaultValues: {
            name: '',
            email: '',
            phone: '',
            clinicName: '',
            subject: SUBJECT_OPTIONS[0],
            message: '',
            website: '',
        },
    })

    const [turnstileToken, setTurnstileToken] = useState('')
    const [turnstileResetKey, setTurnstileResetKey] = useState(0)
    const [status, setStatus] = useState({ type: null, message: null })
    const turnstileRequired = isTurnstileConfigured()

    useEffect(() => {
        const root = document.getElementById('root')
        const previousRootHeight = root?.style.height
        const previousBodyOverflow = document.body.style.overflow

        if (root) root.style.height = 'auto'
        document.body.style.overflow = ''

        return () => {
            if (root) root.style.height = previousRootHeight || ''
            document.body.style.overflow = previousBodyOverflow
        }
    }, [])

    const onSubmit = async (data) => {
        setStatus({ type: null, message: null })

        if (turnstileRequired && !turnstileToken) {
            setStatus({
                type: 'error',
                message: 'Please complete the verification challenge before submitting.',
            })
            return
        }

        try {
            await contactService.submitContact({
                name: data.name.trim(),
                email: data.email.trim(),
                phone: data.phone?.trim() || null,
                clinicName: data.clinicName?.trim() || null,
                subject: data.subject,
                message: data.message.trim(),
                turnstileToken: turnstileToken || null,
                website: data.website || '',
            })

            reset()
            setTurnstileToken('')
            setTurnstileResetKey((key) => key + 1)
            setStatus({
                type: 'success',
                message: 'Thanks! Your message has been sent. Our team will get back to you shortly.',
            })
        } catch (error) {
            console.error(error)
            setTurnstileToken('')
            setTurnstileResetKey((key) => key + 1)
            const apiMessage =
                error?.response?.data?.errorMessage ||
                error?.response?.data?.message ||
                error?.response?.data?.error ||
                'Unable to send your message right now. Please try again or email support directly.'
            setStatus({ type: 'error', message: apiMessage })
        }
    }

    return (
        <div className="contact-page">
            <header className="contact-page__nav">
                <div className="contact-page__nav-inner">
                    <Link to="/login" className="contact-page__brand">
                        <img
                            src={toAbsoluteUrl('media/logos/eyeconx-logo.png')}
                            alt="EyeconX"
                        />
                    </Link>
                    <div className="contact-page__nav-actions">
                        <Link to="/terms-and-conditions" className="contact-page__nav-link">
                            Terms
                        </Link>
                        <Link to="/login" className="contact-page__nav-btn">
                            <i className="bi bi-box-arrow-in-right" />
                            Sign in
                        </Link>
                    </div>
                </div>
            </header>

            <section className="contact-page__hero">
                <div className="contact-page__hero-bg" aria-hidden="true">
                    <span className="contact-page__orb contact-page__orb--1" />
                    <span className="contact-page__orb contact-page__orb--2" />
                    <span className="contact-page__grid" />
                </div>
                <div className="contact-page__hero-inner">
                    <span className="contact-page__eyebrow">
                        <i className="bi bi-chat-dots" />
                        Support
                    </span>
                    <h1>Contact Us</h1>
                    <p>
                        Questions about the Clinic Portal, account access, billing, or patient
                        communications? Send us a message and we’ll help you get sorted.
                    </p>
                </div>
            </section>

            <main className="contact-page__main">
                <div className="contact-page__layout">
                    <aside className="contact-page__aside">
                        <div className="contact-page__info-card">
                            <h2>Reach EyeconX</h2>
                            <p>
                                Prefer email or phone? Use the channels below — we’re here for
                                clinic teams across the EyeconX platform.
                            </p>

                            <a className="contact-page__info-item" href={`mailto:${SUPPORT_EMAIL}`}>
                                <span className="contact-page__info-icon">
                                    <i className="bi bi-envelope" />
                                </span>
                                <span>
                                    <strong>Email</strong>
                                    <em>{SUPPORT_EMAIL}</em>
                                </span>
                            </a>

                            <a className="contact-page__info-item" href={`tel:+18665332669`}>
                                <span className="contact-page__info-icon">
                                    <i className="bi bi-telephone" />
                                </span>
                                <span>
                                    <strong>Phone</strong>
                                    <em>{SUPPORT_PHONE}</em>
                                </span>
                            </a>

                            <div className="contact-page__info-item contact-page__info-item--static">
                                <span className="contact-page__info-icon">
                                    <i className="bi bi-geo-alt" />
                                </span>
                                <span>
                                    <strong>Office</strong>
                                    <em>90 Sheppard Ave E., North York, ON, M2N 3A1, Canada</em>
                                </span>
                            </div>
                        </div>
                    </aside>

                    <div className="contact-page__form-card">
                        <div className="contact-page__form-header">
                            <h2>Send a message</h2>
                            <p>Fill out the form and we’ll respond as soon as we can.</p>
                        </div>

                        {status.message && (
                            <div
                                className={`contact-page__alert contact-page__alert--${status.type}`}
                                role="alert"
                            >
                                <i
                                    className={`bi ${
                                        status.type === 'success'
                                            ? 'bi-check-circle-fill'
                                            : 'bi-exclamation-circle-fill'
                                    }`}
                                />
                                <span>{status.message}</span>
                            </div>
                        )}

                        <form onSubmit={handleSubmit(onSubmit)} noValidate>
                            <div className="contact-page__honeypot" aria-hidden="true">
                                <label htmlFor="website">Website</label>
                                <input
                                    type="text"
                                    id="website"
                                    tabIndex={-1}
                                    autoComplete="off"
                                    {...register('website')}
                                />
                            </div>

                            <div className="contact-page__grid-fields">
                                <div className="contact-page__field">
                                    <label htmlFor="name">Full name</label>
                                    <input
                                        id="name"
                                        type="text"
                                        className={errors.name ? 'is-invalid' : ''}
                                        placeholder="Jane Smith"
                                        {...register('name', { required: 'Name is required' })}
                                    />
                                    {errors.name && (
                                        <span className="contact-page__error">{errors.name.message}</span>
                                    )}
                                </div>

                                <div className="contact-page__field">
                                    <label htmlFor="email">Work email</label>
                                    <input
                                        id="email"
                                        type="email"
                                        className={errors.email ? 'is-invalid' : ''}
                                        placeholder="you@clinic.com"
                                        {...register('email', {
                                            required: 'Email is required',
                                            pattern: {
                                                value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                                                message: 'Enter a valid email address',
                                            },
                                        })}
                                    />
                                    {errors.email && (
                                        <span className="contact-page__error">{errors.email.message}</span>
                                    )}
                                </div>

                                <div className="contact-page__field">
                                    <label htmlFor="phone">Phone <span>(optional)</span></label>
                                    <input
                                        id="phone"
                                        type="tel"
                                        inputMode="numeric"
                                        autoComplete="tel"
                                        maxLength={14}
                                        placeholder="(555) 123-4567"
                                        {...register('phone', {
                                            onChange: (event) => {
                                                const formatted = formatPhoneNumber(event.target.value)
                                                setValue('phone', formatted, {
                                                    shouldDirty: true,
                                                    shouldValidate: true,
                                                })
                                            },
                                            validate: (value) => {
                                                if (!value) return true
                                                const digits = value.replace(/\D/g, '')
                                                return digits.length === 10 || 'Enter a 10-digit phone number'
                                            },
                                        })}
                                    />
                                    {errors.phone && (
                                        <span className="contact-page__error">{errors.phone.message}</span>
                                    )}
                                </div>

                                <div className="contact-page__field">
                                    <label htmlFor="clinicName">Clinic name <span>(optional)</span></label>
                                    <input
                                        id="clinicName"
                                        type="text"
                                        placeholder="Your clinic"
                                        {...register('clinicName')}
                                    />
                                </div>
                            </div>

                            <div className="contact-page__field">
                                <label htmlFor="subject">Subject</label>
                                <select
                                    id="subject"
                                    className={errors.subject ? 'is-invalid' : ''}
                                    {...register('subject', { required: 'Subject is required' })}
                                >
                                    {SUBJECT_OPTIONS.map((option) => (
                                        <option key={option} value={option}>
                                            {option}
                                        </option>
                                    ))}
                                </select>
                                {errors.subject && (
                                    <span className="contact-page__error">{errors.subject.message}</span>
                                )}
                            </div>

                            <div className="contact-page__field">
                                <label htmlFor="message">Message</label>
                                <textarea
                                    id="message"
                                    rows={6}
                                    className={errors.message ? 'is-invalid' : ''}
                                    placeholder="How can we help?"
                                    {...register('message', {
                                        required: 'Message is required',
                                        minLength: {
                                            value: 10,
                                            message: 'Please provide a bit more detail (10+ characters)',
                                        },
                                    })}
                                />
                                {errors.message && (
                                    <span className="contact-page__error">{errors.message.message}</span>
                                )}
                            </div>

                            {turnstileRequired && (
                                <div className="contact-page__turnstile">
                                    <Turnstile
                                        resetKey={turnstileResetKey}
                                        onSuccess={(token) => setTurnstileToken(token)}
                                        onExpire={() => setTurnstileToken('')}
                                        onError={() => setTurnstileToken('')}
                                    />
                                </div>
                            )}

                            <button
                                type="submit"
                                className="contact-page__submit"
                                disabled={isSubmitting || (turnstileRequired && !turnstileToken)}
                            >
                                {isSubmitting ? (
                                    <>
                                        <span className="contact-page__spinner" aria-hidden="true" />
                                        Sending...
                                    </>
                                ) : (
                                    <>
                                        <i className="bi bi-send" />
                                        Send message
                                    </>
                                )}
                            </button>
                        </form>
                    </div>
                </div>
            </main>

            <footer className="contact-page__footer">
                <div className="contact-page__footer-inner">
                    <span>© {new Date().getFullYear()} EyeconX. All rights reserved.</span>
                    <div className="contact-page__footer-links">
                        <Link to="/terms-and-conditions">Terms &amp; Conditions</Link>
                        <Link to="/contact-us">Contact Us</Link>
                        <Link to="/login">Sign in</Link>
                    </div>
                </div>
            </footer>
        </div>
    )
}

export default ContactUs
